import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { AuthContext } from "../context/AuthContext";
import PdfViewerModal from "../components/PdfViewerModal";
import {
  downloadSubmissionPdf,
  downloadSubmissionsZip,
  getSubmissionPdfSource,
  getSubmissionPdfSourceFromServer
} from "../utils/pdf";

const PAGE_SIZE = 10;

const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString([], {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit"
  });
};

const buildPageItems = (totalPages, currentPage) => {
  if (totalPages <= 5) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }
  if (totalPages === 6) {
    return [1, 2, 3, 4, "...", 6];
  }
  const middle = [Math.max(2, currentPage - 1), currentPage, Math.min(totalPages - 1, currentPage + 1)]
    .filter((v, i, arr) => v >= 2 && v <= totalPages - 1 && arr.indexOf(v) === i)
    .sort((a, b) => a - b);
  return [1, "...", ...middle, "...", totalPages].filter(
    (item, index, arr) => !(item === "..." && arr[index - 1] === "...")
  );
};

export default function AdminDashboard() {
  const { fetchAdminSubmissions, admin } = useContext(AuthContext);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [filterValue, setFilterValue] = useState("all");
  const [sortValue, setSortValue] = useState("latest");
  const [selectedIds, setSelectedIds] = useState([]);
  const [viewerSource, setViewerSource] = useState(null);
  const [viewerTitle, setViewerTitle] = useState("");
  const [viewerRow, setViewerRow] = useState(null);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [page, setPage] = useState(1);

  const loadRows = useCallback(async () => {
    setLoading(true);
    setError("");
    const result = await fetchAdminSubmissions();
    if (!result.ok) {
      setRows([]);
      setLoading(false);
      setError(result.error);
      return;
    }
    setRows(Array.isArray(result.data) ? result.data : []);
    setLoading(false);
  }, [fetchAdminSubmissions]);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  const filteredRows = useMemo(() => {
    let nextRows = rows.filter((row) =>
      [
        row.pdfFileName,
        row.name,
        row.email,
        row.correspondenceName,
        row.correspondenceEmail,
        row.phone,
        row.country,
        row.msId,
        row.msTitle,
        row.journalName
      ]
        .join(" ")
        .toLowerCase()
        .includes(search.trim().toLowerCase())
    );

    if (filterValue === "today") {
      const today = new Date().toDateString();
      nextRows = nextRows.filter((row) => new Date(row.createdAt).toDateString() === today);
    } else if (filterValue === "selected") {
      nextRows = nextRows.filter((row) => row.selected);
    }

    if (sortValue === "latest") {
      nextRows.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    } else if (sortValue === "oldest") {
      nextRows.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
    }

    return nextRows;
  }, [rows, search, filterValue, sortValue]);

  const stats = useMemo(() => {
    const today = new Date().toDateString();
    return {
      total: rows.length,
      today: rows.filter((row) => new Date(row.createdAt).toDateString() === today).length,
      countries: new Set(rows.map((row) => row.country).filter(Boolean)).size,
      selected: selectedIds.length
    };
  }, [rows, selectedIds]);

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / PAGE_SIZE));
  const pagedRows = filteredRows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const pageItems = buildPageItems(totalPages, page);
  const pageIds = pagedRows.map((row) => row._id);
  const allPageSelected = pageIds.length > 0 && pageIds.every((id) => selectedIds.includes(id));
  const somePageSelected = pageIds.some((id) => selectedIds.includes(id));

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  useEffect(() => {
    setPage(1);
  }, [search, filterValue, sortValue]);

  const toggleSelectAll = () => {
    const allSelected = pageIds.every((id) => selectedIds.includes(id));
    setSelectedIds((prev) =>
      allSelected ? prev.filter((id) => !pageIds.includes(id)) : Array.from(new Set([...prev, ...pageIds]))
    );
  };

  const closeViewer = () => {
    if (viewerSource?.cleanup) {
      viewerSource.cleanup();
    }
    setViewerOpen(false);
    setViewerSource(null);
    setViewerTitle("");
    setViewerRow(null);
  };

  const openViewer = async (row) => {
    if (viewerSource?.cleanup) {
      viewerSource.cleanup();
    }
    const source =
      getSubmissionPdfSourceFromServer(row, admin?.token) || (await getSubmissionPdfSource(row));
    setViewerTitle(source.fileName);
    setViewerRow(row);
    setViewerSource(source);
    setViewerOpen(true);
  };

  const handleDownload = async (row) => {
    await downloadSubmissionPdf(row, admin?.token);
  };

  const selectedRows = rows.filter((row) => selectedIds.includes(row._id));

  const handleDownloadSelectedZip = async () => {
    if (!selectedRows.length) return;
    await downloadSubmissionsZip(selectedRows, `admin-submissions-${Date.now()}.zip`, admin?.token);
  };

  return (
    <div className="admin-shell">
      <div className="workspace-card section-card">
      <h2>Admin Dashboard</h2>
      <div className="admin-stat-grid">
        <article><span>Total Submissions</span><strong>{stats.total}</strong></article>
        <article><span>Submitted Today</span><strong>{stats.today}</strong></article>
        <article><span>Countries</span><strong>{stats.countries}</strong></article>
        <article><span>Selected</span><strong>{stats.selected}</strong></article>
      </div>

      <div className={`dashboard-toolbar${selectedIds.length > 0 ? " dashboard-toolbar-selected" : ""}`}>
        <input className="toolbar-input" placeholder="Real-time search" value={search} onChange={(e) => setSearch(e.target.value)} />
        <select className="toolbar-input" value={filterValue} onChange={(e) => setFilterValue(e.target.value)}>
          <option value="all">Filter: All</option>
          <option value="today">Filter: Today</option>
          <option value="selected">Filter: Selected</option>
        </select>
        <select className="toolbar-input" value={sortValue} onChange={(e) => setSortValue(e.target.value)}>
          <option value="latest">Sort: Latest</option>
          <option value="oldest">Sort: Oldest</option>
        </select>
        {selectedIds.length > 0 && (
          <>
            <button className="toolbar-btn" onClick={handleDownloadSelectedZip}>Download ZIP</button>
          </>
        )}
      </div>

      {loading && <p className="page-muted">Loading dashboard...</p>}
      {error && <p className="submit-error">{error}</p>}

      {!loading && !error && (
        <div className="table-wrap">
          <table className="dashboard-table">
            <thead>
              <tr>
                <th>
                  <input
                    type="checkbox"
                    checked={allPageSelected}
                    ref={(node) => {
                      if (node) node.indeterminate = !allPageSelected && somePageSelected;
                    }}
                    onChange={toggleSelectAll}
                  />
                </th>
                <th>SNo</th>
                <th>Date</th>
                <th>Journal Name</th>
                <th>Author Name</th>
                <th>Email</th>
                <th>ID</th>
                <th>Title</th>
                <th>Files</th>
              </tr>
            </thead>
            <tbody>
              {pagedRows.map((row, index) => (
                <tr key={row._id}>
                  <td><input type="checkbox" checked={selectedIds.includes(row._id)} onChange={() => setSelectedIds((prev) => prev.includes(row._id) ? prev.filter((id) => id !== row._id) : [...prev, row._id])} /></td>
                  <td>{(page - 1) * PAGE_SIZE + index + 1}</td>
                  <td>{formatDate(row.createdAt)}</td>
                  <td>{row.journalName || "-"}</td>
                  <td>{row.correspondenceName || row.name || "-"}</td>
                  <td>{row.correspondenceEmail || row.email || "-"}</td>
                  <td>{row.msId || "-"}</td>
                  <td>{row.msTitle || "-"}</td>
                  <td className="action-cell">
                    <div className="action-btn-group">
                      <button
                        className="toolbar-btn compact-btn"
                        onClick={async () => {
                          await openViewer(row);
                        }}
                      >
                        View
                      </button>
                      <button className="toolbar-btn compact-btn" onClick={() => handleDownload(row)}>
                        Download
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {pagedRows.length === 0 && (
                <tr><td colSpan={9} className="empty-state">No entries found.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

        <div className="pagination-row">
          <p>Page {page} of {totalPages}</p>
          <div className="pagination-controls">
            <button onClick={() => setPage(1)} disabled={page === 1}>{`<<`}</button>
            <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>{`<`}</button>
            {pageItems.map((item, index) =>
              item === "..." ? (
                <span key={`dots-${index}`} className="page-dots">...</span>
              ) : (
                <button key={item} className={item === page ? "active" : ""} onClick={() => setPage(Number(item))}>{item}</button>
              )
            )}
            <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages}>{`>`}</button>
            <button onClick={() => setPage(totalPages)} disabled={page === totalPages}>{`>>`}</button>
          </div>
        </div>
      </div>
      <PdfViewerModal
        open={viewerOpen}
        title={viewerTitle}
        source={viewerSource}
        onClose={closeViewer}
        onDownload={() => {
          if (!viewerRow) return;
          handleDownload(viewerRow);
        }}
      />
    </div>
  );
}

